import * as Sentry from '@sentry/react';
import { envUtils } from './utils';
import { AuthenticationResponse } from '@activepieces/shared';

export const sentry = {
  get environment() {
    return import.meta.env.VITE_APP_ENVIRONMENT || import.meta.env.MODE;
  },

  init: (): void => {
    if (!import.meta.env.VITE_SENTRY_DSN || sentry.environment === 'dev') {
      return;
    }

    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: sentry.environment,
      integrations: (defaultIntegrations) => [
        ...defaultIntegrations,
        // Browser tracing for performance monitoring
        Sentry.browserTracingIntegration(),
      ],

      // Performance Monitoring
      tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE
        ? parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE)
        : 1.0,

      // Filter out errors that should not be sent to Sentry
      beforeSend: (event) => {
        // Filter network errors (can be noisy)
        if (event?.exception?.values?.[0]?.type === 'NetworkError') {
          return null;
        }

        // Filter out specific error messages if needed
        const errorMessage = event?.exception?.values?.[0]?.value;
        if (
          errorMessage &&
          ['EXECUTION_TIMEOUT', 'ENTITY_NOT_FOUND'].includes(errorMessage)
        ) {
          return null;
        }

        return event;
      },
    });
  },

  captureException: (
    error: unknown,
    title?: string,
    context?: {
      tags?: Record<string, string>;
      extra?: Record<string, unknown>;
    },
  ): void => {
    const errorToSend =
      error instanceof Error ? error : new Error(String(error));

    if (title) {
      errorToSend.name = title;
    }

    Sentry.captureException(errorToSend, {
      tags: context?.tags,
      extra: context?.extra,
    });

    if (sentry.environment === 'dev') {
      console.error('[Sentry Error Captured]', {
        name: errorToSend.name,
        message: errorToSend.message,
        tags: context?.tags,
        extra: context?.extra,
      });
    }
  },

  captureMessage: (
    message: string,
    level: 'info' | 'warning' | 'error' = 'info',
    context?: {
      tags?: Record<string, string>;
      extra?: Record<string, unknown>;
    },
  ): void => {
    Sentry.captureMessage(message, {
      level,
      tags: context?.tags,
      extra: context?.extra,
    });

    if (sentry.environment === 'dev') {
      const consoleLevel = level === 'warning' ? 'warn' : level;
      console[consoleLevel as 'error' | 'warn' | 'info']('[Sentry Message]', message, context);
    }
  },

  setUser: ({
    id,
    externalId: kizeoFormsId,
    firstName,
    lastName,
    email,
  }: Pick<
    AuthenticationResponse,
    'id' | 'externalId' | 'firstName' | 'lastName' | 'email'
  >): void => {
    Sentry.setUser({
      id,
      kizeoFormsId,
      firstName,
      lastName,
      email,
    });
  },

  clearUser: (): void => {
    Sentry.setUser(null);
  },
};
